/**
 * Every number that affects how the game *feels*, in one place.
 *
 * Physics runs in fixed "world units" rather than pixels, so behaviour is identical on
 * a phone and a desktop — only the scale factor at draw time differs. The board is
 * always WORLD_WIDTH wide; its height adapts to the screen within a clamped range, so
 * the game fills the viewport without ever becoming a different game.
 */

export const WORLD_WIDTH = 480;

/**
 * Board height range, as a multiple of width.
 *
 * Capped fairly low on purpose: a tall thin jar takes forever to fill and makes the
 * balls look small. Past the cap the extra screen height becomes margin around the
 * board rather than more jar.
 */
export const MIN_ASPECT = 1.15;
export const MAX_ASPECT = 1.6;

/** Physics steps at a fixed rate regardless of display refresh rate. */
export const TICK_MS = 1000 / 60;
/** Cap on catch-up steps per frame, so a slow frame can't spiral into a freeze. */
export const MAX_TICKS_PER_FRAME = 5;

export const WALL_THICKNESS = 60;

// --- Body tuning ---

export const GRAVITY_Y = 1.35;
/** Low bounce: Suika balls settle, they don't ping around. */
export const RESTITUTION = 0.12;
export const FRICTION = 0.44;
export const FRICTION_STATIC = 0.62;
/** A whisper of air drag so stacks come to rest instead of jittering forever. */
export const FRICTION_AIR = 0.004;
/**
 * Constant density means mass scales with area, so a big ball genuinely is heavier
 * and shoves smaller ones aside. This is most of why the game feels physical.
 */
export const DENSITY = 0.001;

// --- Dropping ---

/** Height above the board top where the held ball waits, in world units. */
export const DROP_Y = 62;
/** Distance below the board top where the danger line sits. */
export const FILL_LINE_Y = 128;
/** Delay before the next ball becomes droppable. Suika uses roughly this. */
export const DROP_COOLDOWN_MS = 420;
/**
 * Maximum random spin on release, in radians per physics step.
 *
 * Only there to break the symmetry of a perfectly-stacked column. Keep it small: at
 * 60 steps a second, 0.1 here is already a full revolution per second, which reads as
 * the ball being flicked rather than dropped — and once these are photos of faces, a
 * spinning ball means an upside-down face.
 */
export const DROP_SPIN = 0.012;

// --- Merging ---

/** Upward pop given to a freshly merged ball, scaled by its radius. */
export const MERGE_POP = 0.0016;
/** Duration of the merge flash/ring effect. */
export const MERGE_FX_MS = 420;

/**
 * How long a ball born from a merge waits before its next merge is *carried out*.
 *
 * Without this, a cascade resolves in a handful of frames — four merges finish inside
 * ~60ms and you see one blur instead of a chain. Staggering them makes each step
 * register. Kept just under MERGE_FX_MS so the previous pop is still fading as the
 * next fires, which reads as one flowing chain rather than a series of stutters.
 * Raise it for a slower, more deliberate cascade; lower it for a snappier one.
 *
 * Note this delays execution, not eligibility: the pair is committed the moment it
 * touches. Blocking eligibility instead lets balls bounce apart during the wait and
 * silently breaks the chain.
 */
export const MERGE_CHAIN_DELAY_MS = 300;

/**
 * Pull applied between a committed pair while it waits out the delay above.
 *
 * Keeps the two balls visibly together — and telegraphs what's about to merge —
 * rather than letting them drift apart in the gap. Scaled by mass at the call site so
 * a big pair converges just as briskly as a small one.
 */
export const MERGE_ATTRACT = 0.0009;

// --- Spawn mix ---

/**
 * How strongly the early game favours the smallest droppable ranks.
 *
 * Opening on a balanced mix hands you big balls before there's anything to merge them
 * with, which wastes board space you can't recover. This decays to zero over
 * SPAWN_BIAS_DROPS so the mix evens out once a ladder exists.
 */
export const SPAWN_BIAS_START = 0.7;
export const SPAWN_BIAS_DROPS = 35;

// --- Game over ---

/**
 * A ball must *rest* above the line this long before the game ends. Ending the moment
 * anything crosses feels punishing and isn't how Suika behaves — balls legitimately
 * bounce up past the line during play.
 */
export const OVERFLOW_GRACE_MS = 2000;
/** Below this speed a ball counts as settled rather than in flight. */
export const SETTLED_SPEED = 0.9;
/** A ball can't trigger game over until it has existed this long. */
export const SPAWN_IMMUNITY_MS = 700;
